'use client'

import { ChevronRight } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'
import { useGetCityBrandsQuery } from '@/api/brands/get-brand-query'
import { RestaurantCityBrandsLink } from '@/features/restaurants/restaurants-city-search/restaurant-city-brands-link'
import { useRestaurantCitySearchContext } from '@/features/restaurants/restaurants-city-search/utils/restaurant-city-search-context'
import { Locale } from '@/setup/locales'
import { getLinkHref } from '@/setup/routing'
import { createImageUrl } from '@/utils/image/create-image-url'

/** Below this many brands a chip row is not worth its space */
const MIN_BRANDS_FOR_ROW = 3

const MAX_BRANDS_SHOWN = 10

/**
 * Quiet brand directory row in the SEO zone: the city's biggest brands as
 * chip links into their brand pages, giving every city page crawlable paths
 * into the brands world. Falls back to the plain hub link in small cities.
 */
export const RestaurantCityBrandsRow = ({ citySlug }: { citySlug: string }) => {
    const locale = useLocale() as Locale
    const t = useTranslations()

    const { city } = useRestaurantCitySearchContext()

    // Prefetched and hydrated by the server component, so this reads from cache.
    const { data: brandsData } = useGetCityBrandsQuery(citySlug)

    const brands = brandsData?.data

    if (!brands?.length) return null

    if (brands.length < MIN_BRANDS_FOR_ROW) {
        return <RestaurantCityBrandsLink citySlug={citySlug} />
    }

    const shownBrands = [...brands]
        .sort((a, b) => b.locationCount - a.locationCount)
        .slice(0, MAX_BRANDS_SHOWN)

    return (
        <div className="flex flex-col gap-y-3">
            {city?.name && (
                <h2 className="text-h4 md:text-h3">
                    <Link
                        href={getLinkHref(
                            { pattern: '/restaurants/[citySlug]/brands', params: { citySlug } },
                            locale
                        )}
                        aria-label={t('restaurants.brands.cityLink')}
                        className="group flex w-fit items-center gap-x-1 webkit-tap-transparent"
                    >
                        {t('restaurants.brands.title', { cityName: city.name })}

                        <ChevronRight
                            className="size-5 shrink-0 text-fg-tertiary transition-transform
                                duration-150 group-hover:translate-x-0.5"
                        />
                    </Link>
                </h2>
            )}

            <div className="flex flex-wrap items-center gap-2">
                {shownBrands.map((brand) => (
                    <Link
                        key={'city-brands-row' + brand.slug}
                        href={getLinkHref(
                            {
                                pattern: '/restaurants/[citySlug]/brands/[brandSlug]',
                                params: { citySlug, brandSlug: brand.slug },
                            },
                            locale
                        )}
                        className="flex h-10 items-center gap-x-2 rounded-full bg-bg-neutral-surface
                            px-3 text-label-sm text-fg-primary whitespace-nowrap
                            webkit-tap-transparent transition-colors duration-150
                            hover:bg-bg-neutral-surface-medium"
                    >
                        {brand.logoUrl && (
                            <Image
                                unoptimized
                                src={createImageUrl(brand.logoUrl, { width: 20, height: 20 })}
                                alt=""
                                width={20}
                                height={20}
                                className="size-5 shrink-0 rounded-full object-cover"
                            />
                        )}

                        {brand.name}
                    </Link>
                ))}
            </div>
        </div>
    )
}
